import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, CONTACT } from "@/lib/api";
import { BadgeCheck, MapPin, ArrowLeft, Phone, MessageCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const resolveUrl = (u) => {
  if (!u) return "/coming-soon.svg";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const onImgError = (e) => {
  if (e.target.src.indexOf("coming-soon.svg") === -1) {
    e.target.src = "/coming-soon.svg";
  }
};

export default function CompanionDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get(`/companions/${id}`).then((r) => setC(r.data));
  }, [id]);

  if (!c) {
    return (
      <div className="pt-40 text-center text-white/50 label-luxury">{t("loading")}</div>
    );
  }

  return (
    <div data-testid="companion-detail" className="pt-28 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Link
          to="/companions"
          data-testid="back-to-directory"
          className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white/60 hover-gold mb-10"
        >
          <ArrowLeft size={12} /> {t("back_to_directory")}
        </Link>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12">
          <div>
            <div className="aspect-[3/4] overflow-hidden bg-black mb-4 relative">
              <img
                src={resolveUrl(c.photos[active] || c.cover_photo)}
                alt={c.name}
                onError={onImgError}
                className="w-full h-full object-cover"
                data-testid="companion-main-photo"
              />
              <div className="beb-watermark beb-watermark-large" aria-hidden="true">
                BILLION EURO BABIES
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {c.photos.map((p, i) => (
                <button
                  key={p}
                  onClick={() => setActive(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`aspect-square overflow-hidden border ${
                    active === i
                      ? "border-[var(--gold)]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <img src={resolveUrl(p)} onError={onImgError} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-3">
              {c.verified && (
                <span className="flex items-center gap-1 text-[var(--gold)] label-luxury">
                  <BadgeCheck size={12} /> {t("verified")}
                </span>
              )}
              {c.featured && (
                <span className="vip-badge" style={{boxShadow: "0 0 20px rgba(232,180,201,0.4)"}}>
                  VIP
                </span>
              )}
            </div>
            <h1 className="font-serif-display text-6xl md:text-7xl font-light tracking-tight leading-none companion-name-luxury gold-glow">
              {c.name}
            </h1>
            <div className="monogram-divider mt-3 mb-1"><span>❖</span></div>
            <div className="flex items-center gap-3 text-white/60 text-sm tracking-wider">
              <span>{c.age} {t("years")}</span>
              <span className="text-[var(--gold)]">·</span>
              <span>{c.nationality}</span>
              <span className="text-[var(--gold)]">·</span>
              <span className="flex items-center gap-1">
                <MapPin size={12} /> {c.location}
              </span>
            </div>

            <div className="my-8 flex items-baseline gap-3">
              <span className="font-serif-display text-6xl price-big">€{c.price}</span>
              <span className="label-pink">{t("per_hour")}</span>
            </div>

            {c.rates && c.rates.length > 0 && (
              <div className="mb-8">
                <div className="label-pink mb-4 text-center tracking-[0.4em]">◆ Rates ◆</div>
                <div className="rates-luxury">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-px">
                    {c.rates.map((r) => (
                      <div key={r.duration} className="rate-cell">
                        <div className="duration">{r.duration}</div>
                        <div className="price">
                          <span className="price-currency">€</span>{r.price}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <p className="text-white/70 leading-relaxed text-[15px] italic font-serif-display">{c.description}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 mt-10 border border-white/5">
              {[
                { l: t("height"), v: `${c.height} cm` },
                { l: t("measurements"), v: c.measurements || "—" },
                { l: t("hair_label"), v: c.hair },
                { l: t("languages"), v: c.languages?.length || 0 },
              ].map((s) => (
                <div key={s.l} className="bg-[#0a0a0a] p-5">
                  <div className="label-luxury text-[0.6rem]">{s.l}</div>
                  <div className="font-serif-display text-xl mt-2">{s.v}</div>
                </div>
              ))}
            </div>

            {c.languages?.length > 0 && (
              <div className="mt-8">
                <div className="label-luxury mb-3">{t("languages")}</div>
                <div className="flex flex-wrap gap-2">
                  {c.languages.map((l) => (
                    <span
                      key={l}
                      className="border border-white/15 px-3 py-1.5 text-xs tracking-wider uppercase"
                    >
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {c.services?.length > 0 && (
              <div className="mt-8">
                <div className="label-luxury mb-3">{t("services_label")}</div>
                <div className="flex flex-wrap gap-2">
                  {c.services.map((s) => (
                    <span
                      key={s}
                      data-testid={`service-tag-${s}`}
                      className="border border-[var(--pink)]/40 text-[var(--pink)] px-3 py-1.5 text-xs tracking-wider uppercase hover:border-[var(--pink)] hover:bg-[var(--pink)]/5 transition-colors"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {c.restrictions?.length > 0 && (
              <div className="mt-6">
                <div className="label-luxury mb-3 text-red-300/70">Does Not Offer</div>
                <div className="flex flex-wrap gap-2">
                  {c.restrictions.map((r) => (
                    <span
                      key={r}
                      data-testid={`restriction-tag-${r}`}
                      className="border border-red-400/30 text-red-300/80 px-3 py-1.5 text-xs tracking-wider uppercase line-through decoration-red-400/40"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex gap-3 flex-wrap">
              <span className="vip-badge" data-testid="outcall-only-badge" style={{boxShadow: "0 0 20px rgba(232,180,201,0.4)"}}>
                ◆ Outcall Only
              </span>
              <span className="border border-white/15 text-white/70 px-3 py-1.5 text-[0.6rem] tracking-[0.25em] uppercase">
                Comes to your location
              </span>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(`Hello, I would like to book ${c.name}.`)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="book-whatsapp"
                className="btn-gold btn-gold-solid"
              >
                <MessageCircle size={14} /> {t("book_whatsapp")}
              </a>
              <a
                href={`tel:${CONTACT.phone}`}
                data-testid="book-call"
                className="btn-gold"
              >
                <Phone size={14} /> {t("book_call")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
